/**
 * PeopleGraph v1.0 — Style Theme
 * All component styles in a single theme object.
 */

export const styles = {
  // Loading
  loadingScreen: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    background: "#141416",
    fontFamily: "'DM Sans', sans-serif",
  },
  loadingLogo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 32,
  },
  logoIcon: { fontSize: 28, color: "#C2785C", fontWeight: 700 },
  logoText: {
    fontSize: 24,
    fontWeight: 700,
    color: "#e8e4df",
    letterSpacing: "-0.5px",
  },
  loadingSpinner: {
    width: 32,
    height: 32,
    border: "3px solid #2a2a2e",
    borderTopColor: "#C2785C",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  // Auth
  authContainer: {
    display: "flex",
    height: "100vh",
    fontFamily: "'DM Sans', sans-serif",
    background: "#141416",
    color: "#e8e4df",
  },
  authDecoration: {
    flex: 1,
    background:
      "linear-gradient(135deg, #1a1a1e 0%, #1e2a26 50%, #1a1a1e 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 48,
  },
  authDecoInner: { maxWidth: 400, textAlign: "center" },
  authDecoTitle: {
    fontSize: 36,
    fontWeight: 700,
    marginTop: 16,
    color: "#e8e4df",
    letterSpacing: "-1px",
  },
  authDecoSub: {
    fontSize: 16,
    color: "#9a9a9e",
    marginTop: 12,
    lineHeight: 1.6,
  },
  authDecoNodes: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "center",
    marginTop: 32,
  },
  authDecoNode: {
    padding: "8px 16px",
    borderRadius: 20,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    animation: "fadeIn 0.6s ease both",
  },
  authFormSide: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 48,
  },
  authFormContainer: { width: "100%", maxWidth: 380 },
  authTitle: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 6,
    color: "#e8e4df",
  },
  authSubtitle: { fontSize: 15, color: "#7a7a7e", marginBottom: 28 },
  authError: {
    background: "#3a1a1a",
    border: "1px solid #6a2a2a",
    borderRadius: 8,
    padding: "10px 14px",
    color: "#e88",
    fontSize: 13,
    marginBottom: 16,
  },
  authSubmitBtn: {
    width: "100%",
    padding: "13px 0",
    background: "#C2785C",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 8,
    transition: "all 0.2s",
  },
  authSwitch: {
    textAlign: "center",
    marginTop: 20,
    fontSize: 14,
    color: "#7a7a7e",
  },
  authSwitchLink: { color: "#C2785C", cursor: "pointer", fontWeight: 600 },

  // App Layout
  appContainer: {
    display: "flex",
    height: "100vh",
    fontFamily: "'DM Sans', sans-serif",
    background: "#141416",
    color: "#e8e4df",
    overflow: "hidden",
  },
  sidebar: {
    width: 240,
    background: "#1a1a1e",
    borderRight: "1px solid #2a2a2e",
    display: "flex",
    flexDirection: "column",
    transition: "width 0.3s ease",
    overflow: "hidden",
    flexShrink: 0,
  },
  sidebarCollapsed: { width: 64 },
  sidebarHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "20px 18px",
    borderBottom: "1px solid #2a2a2e",
  },
  sidebarLogoText: {
    fontSize: 18,
    fontWeight: 700,
    color: "#e8e4df",
    whiteSpace: "nowrap",
  },
  nav: {
    flex: 1,
    padding: "12px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "11px 14px",
    border: "none",
    background: "transparent",
    color: "#9a9a9e",
    fontSize: 14,
    borderRadius: 8,
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    transition: "all 0.15s",
    whiteSpace: "nowrap",
  },
  navItemActive: { background: "#2a2a2e", color: "#e8e4df" },
  navIcon: { fontSize: 18, width: 24, textAlign: "center", flexShrink: 0 },
  sidebarFooter: { borderTop: "1px solid #2a2a2e", padding: 14 },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },
  userAvatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "#4A7C6F",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    color: "#fff",
    flexShrink: 0,
  },
  userDetails: { overflow: "hidden" },
  userName: {
    fontSize: 13,
    fontWeight: 600,
    color: "#e8e4df",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userEmail: {
    fontSize: 11,
    color: "#7a7a7e",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  logoutBtn: {
    width: "100%",
    padding: "8px 0",
    border: "1px solid #2a2a2e",
    background: "transparent",
    color: "#9a9a9e",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
    fontFamily: "inherit",
  },

  // Main content
  mainContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "16px 24px",
    borderBottom: "1px solid #2a2a2e",
    background: "#1a1a1e",
    flexShrink: 0,
  },
  menuBtn: {
    background: "none",
    border: "none",
    color: "#9a9a9e",
    fontSize: 20,
    cursor: "pointer",
    padding: "4px 8px",
  },
  pageTitle: { flex: 1, fontSize: 20, fontWeight: 700, color: "#e8e4df" },
  addBtn: {
    padding: "9px 20px",
    background: "#C2785C",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    transition: "all 0.2s",
  },
  content: { flex: 1, overflow: "auto", padding: 24 },

  // Dashboard
  dashboard: { maxWidth: 1100, margin: "0 auto" },
  statCards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 16,
    marginBottom: 24,
  },
  statCard: {
    background: "#1a1a1e",
    borderRadius: 12,
    padding: "20px 24px",
    borderLeft: "4px solid #C2785C",
  },
  statValue: { fontSize: 32, fontWeight: 700, color: "#e8e4df" },
  statLabel: { fontSize: 13, color: "#7a7a7e", marginTop: 4 },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: 16,
  },
  chartCard: { background: "#1a1a1e", borderRadius: 12, padding: 20 },
  chartTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#e8e4df",
    marginBottom: 16,
  },

  // Pie legend
  pieLegend: { display: "flex", flexDirection: "column", gap: 8 },
  pieLegendItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#c8c4bf",
  },
  pieLegendDot: { width: 10, height: 10, borderRadius: 3, flexShrink: 0 },
  pieLegendCount: { marginLeft: "auto", fontWeight: 600, color: "#e8e4df" },

  // Recent
  recentList: { display: "flex", flexDirection: "column", gap: 10 },
  recentItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 6px",
    cursor: "pointer",
    borderRadius: 8,
    transition: "background 0.15s",
  },
  recentAvatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    color: "#fff",
    flexShrink: 0,
  },
  recentInfo: { overflow: "hidden" },
  recentName: { fontSize: 14, fontWeight: 600, color: "#e8e4df" },
  recentMeta: { fontSize: 12, color: "#7a7a7e" },
  recentArrow: {
    color: "#555",
    fontSize: 16,
    flexShrink: 0,
    marginLeft: "auto",
  },
  viewAllBtn: {
    marginTop: 12,
    background: "none",
    border: "1px solid #2a2a2e",
    borderRadius: 8,
    padding: "8px 16px",
    color: "#C2785C",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    width: "100%",
  },

  // Back button
  backBtn: {
    background: "none",
    border: "1px solid #2a2a2e",
    borderRadius: 6,
    padding: "4px 12px",
    color: "#C2785C",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: 500,
  },

  // Empty state
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 80,
    textAlign: "center",
  },
  emptyIcon: { fontSize: 56, marginBottom: 16 },
  emptyTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#e8e4df",
    marginBottom: 8,
  },
  emptyText: { fontSize: 15, color: "#7a7a7e", maxWidth: 420, lineHeight: 1.5 },
  emptyHints: { display: "flex", gap: 10, marginTop: 24 },
  emptyHint: {
    padding: "8px 16px",
    border: "1px solid #333",
    borderRadius: 20,
    fontSize: 13,
    color: "#9a9a9e",
  },

  // Tree
  treeContainer: {
    position: "relative",
    height: "calc(100vh - 140px)",
    background: "#141416",
    borderRadius: 12,
    overflow: "hidden",
  },
  zoomControls: {
    position: "absolute",
    top: 16,
    right: 16,
    display: "flex",
    alignItems: "center",
    gap: 6,
    zIndex: 10,
    background: "#1a1a1e",
    borderRadius: 8,
    padding: "4px 8px",
    border: "1px solid #2a2a2e",
  },
  zoomBtn: {
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "1px solid #333",
    borderRadius: 6,
    color: "#e8e4df",
    cursor: "pointer",
    fontSize: 16,
  },
  zoomLabel: {
    fontSize: 12,
    color: "#7a7a7e",
    minWidth: 40,
    textAlign: "center",
  },

  // People List
  peopleList: { maxWidth: 1100, margin: "0 auto" },
  filters: { marginBottom: 20 },
  searchWrapper: { position: "relative", marginBottom: 12 },
  searchIcon: {
    position: "absolute",
    left: 14,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 16,
  },
  searchInput: {
    width: "100%",
    padding: "12px 14px 12px 42px",
    background: "#1a1a1e",
    border: "1px solid #2a2a2e",
    borderRadius: 10,
    fontSize: 14,
    color: "#e8e4df",
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  },
  categoryFilter: { display: "flex", gap: 8, flexWrap: "wrap" },
  filterChip: {
    padding: "6px 14px",
    border: "1px solid #2a2a2e",
    background: "transparent",
    borderRadius: 20,
    color: "#9a9a9e",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.15s",
  },
  filterChipActive: {
    background: "#C2785C",
    color: "#fff",
    borderColor: "#C2785C",
  },
  peopleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 14,
  },
  personCard: {
    background: "#1a1a1e",
    borderRadius: 12,
    padding: 18,
    cursor: "pointer",
    transition: "all 0.2s",
    border: "1px solid #2a2a2e",
  },
  personCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  personAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 700,
    color: "#fff",
    flexShrink: 0,
  },
  personCardName: { fontSize: 16, fontWeight: 600, color: "#e8e4df" },
  personCardRelation: { fontSize: 13, color: "#7a7a7e" },
  personCardMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  personCardCategory: {
    fontSize: 12,
    padding: "3px 10px",
    border: "1px solid",
    borderRadius: 12,
    color: "#c8c4bf",
  },
  personCardDate: { fontSize: 12, color: "#7a7a7e" },
  personCardDetail: { fontSize: 13, color: "#9a9a9e", marginBottom: 4 },
  personCardNotes: {
    fontSize: 13,
    color: "#7a7a7e",
    fontStyle: "italic",
    marginTop: 8,
    lineHeight: 1.5,
  },
  personCardActions: {
    display: "flex",
    gap: 8,
    marginTop: 12,
    borderTop: "1px solid #2a2a2e",
    paddingTop: 10,
  },
  cardActionBtn: {
    background: "none",
    border: "none",
    color: "#9a9a9e",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    padding: "4px 8px",
  },

  // Modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 24,
    backdropFilter: "blur(4px)",
  },
  modal: {
    background: "#1a1a1e",
    borderRadius: 16,
    width: "100%",
    maxWidth: 600,
    maxHeight: "90vh",
    overflow: "auto",
    border: "1px solid #2a2a2e",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid #2a2a2e",
  },
  modalTitle: { fontSize: 20, fontWeight: 700, color: "#e8e4df" },
  modalClose: {
    background: "none",
    border: "none",
    color: "#7a7a7e",
    fontSize: 20,
    cursor: "pointer",
    padding: "4px 8px",
  },
  modalBody: { padding: 24 },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "16px 24px",
    borderTop: "1px solid #2a2a2e",
  },

  // Form
  formRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
  fieldGroup: { marginBottom: 16 },
  fieldLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#9a9a9e",
    marginBottom: 6,
  },
  labelWithAction: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  todayBtn: {
    background: "none",
    border: "1px solid #3a3a3e",
    borderRadius: 4,
    padding: "1px 8px",
    fontSize: 11,
    color: "#C2785C",
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: 600,
    lineHeight: "18px",
    transition: "all 0.15s",
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    background: "#141416",
    border: "1px solid #2a2a2e",
    borderRadius: 8,
    fontSize: 14,
    color: "#e8e4df",
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  },
  categoryPicker: { display: "flex", flexWrap: "wrap", gap: 8 },
  categoryPickerBtn: {
    padding: "7px 14px",
    border: "1px solid #2a2a2e",
    background: "transparent",
    borderRadius: 8,
    color: "#9a9a9e",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.15s",
  },
  cancelBtn: {
    padding: "10px 20px",
    background: "transparent",
    border: "1px solid #2a2a2e",
    borderRadius: 8,
    color: "#9a9a9e",
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  saveBtn: {
    padding: "10px 24px",
    background: "#C2785C",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    padding: "6px 0",
  },
  checkbox: {
    width: 18,
    height: 18,
    accentColor: "#7B68AE",
    cursor: "pointer",
  },
  checkboxLabel: { fontSize: 14, color: "#c8c4bf", fontWeight: 500 },

  // Profile upload
  profileUploadArea: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "#222",
    border: "2px dashed #3a3a3e",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
  },
  profilePreview: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
  },
  profilePlaceholder: { fontSize: 24, opacity: 0.4 },
  fileInput: { fontSize: 12, color: "#9a9a9e", marginTop: 4, width: "100%" },
  removePhotoBtn: {
    background: "none",
    border: "none",
    color: "#e06060",
    fontSize: 12,
    cursor: "pointer",
    padding: "4px 0",
    fontFamily: "inherit",
  },

  // Nights together
  nightsAddRow: { display: "flex", gap: 8, alignItems: "center", marginTop: 6 },
  nightsAddBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    border: "1px solid #4A7C6F",
    background: "#4A7C6F33",
    color: "#4A7C6F",
    fontSize: 18,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  nightsList: {
    marginTop: 10,
    background: "#141416",
    borderRadius: 8,
    padding: 8,
    maxHeight: 180,
    overflowY: "auto",
  },
  nightsItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 0",
    borderBottom: "1px solid #222",
  },
  nightsInput: {
    padding: "5px 8px",
    background: "#1a1a1e",
    border: "1px solid #2a2a2e",
    borderRadius: 6,
    fontSize: 12,
    color: "#e8e4df",
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  },
  nightsRemoveBtn: {
    background: "none",
    border: "none",
    color: "#e06060",
    cursor: "pointer",
    fontSize: 13,
    padding: "2px 6px",
    flexShrink: 0,
  },
  nightsCount: {
    textAlign: "right",
    fontSize: 11,
    color: "#7a7a7e",
    marginTop: 6,
    padding: "0 4px",
  },
  nightsDetailList: {
    background: "#141416",
    borderRadius: 8,
    padding: 10,
    maxHeight: 160,
    overflowY: "auto",
  },
  nightsDetailItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "5px 0",
    borderBottom: "1px solid #1e1e22",
  },
  nightsDetailDate: {
    fontSize: 13,
    color: "#c8c4bf",
    fontWeight: 500,
    minWidth: 110,
  },
  nightsDetailLabel: { fontSize: 12, color: "#7a7a7e", fontStyle: "italic" },

  // Bulk add
  bulkAddBtn: {
    padding: "9px 16px",
    background: "transparent",
    border: "1px solid #C2785C",
    color: "#C2785C",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    transition: "all 0.2s",
  },
  bulkHeaderRow: {
    display: "flex",
    gap: 8,
    padding: "0 0 8px 0",
    borderBottom: "1px solid #2a2a2e",
    marginBottom: 6,
    alignItems: "center",
  },
  bulkColLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#7a7a7e",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  bulkRowsContainer: { maxHeight: 360, overflowY: "auto", marginBottom: 12 },
  bulkRow: {
    display: "flex",
    gap: 8,
    padding: "6px 0",
    alignItems: "center",
    borderBottom: "1px solid #1e1e22",
  },
  bulkInput: {
    padding: "8px 10px",
    background: "#141416",
    border: "1px solid #2a2a2e",
    borderRadius: 6,
    fontSize: 13,
    color: "#e8e4df",
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    minWidth: 0,
  },
  bulkRemoveBtn: {
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    color: "#555",
    cursor: "pointer",
    fontSize: 13,
    borderRadius: 4,
    flexShrink: 0,
    transition: "color 0.15s",
  },
  bulkAddRowBtn: {
    width: "100%",
    padding: "10px 0",
    background: "transparent",
    border: "1px dashed #3a3a3e",
    borderRadius: 8,
    color: "#7a7a7e",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.15s",
  },

  // Backup & Restore
  backupContainer: {
    maxWidth: 700,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  backupCard: {
    display: "flex",
    gap: 20,
    background: "#1a1a1e",
    borderRadius: 14,
    padding: 28,
    border: "1px solid #2a2a2e",
  },
  backupCardIcon: {
    fontSize: 36,
    flexShrink: 0,
    width: 48,
    textAlign: "center",
    paddingTop: 2,
  },
  backupCardContent: { flex: 1, minWidth: 0 },
  backupCardTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#e8e4df",
    marginBottom: 6,
  },
  backupCardDesc: {
    fontSize: 14,
    color: "#7a7a7e",
    lineHeight: 1.55,
    marginBottom: 4,
  },
  backupInfoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    marginTop: 14,
  },
  backupInfoItem: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "8px 12px",
    background: "#141416",
    borderRadius: 8,
  },
  backupInfoLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#7a7a7e",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  backupInfoValue: {
    fontSize: 14,
    fontWeight: 600,
    color: "#e8e4df",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  backupHint: {
    fontSize: 12,
    color: "#555",
    marginTop: 8,
    fontStyle: "italic",
  },
  backupUploadBtn: {
    padding: "11px 24px",
    background: "transparent",
    border: "2px dashed #3a3a3e",
    borderRadius: 10,
    color: "#9a9a9e",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.2s",
    width: "100%",
    textAlign: "center",
  },
  backupError: {
    marginTop: 12,
    padding: "10px 14px",
    background: "#3a1a1a",
    border: "1px solid #6a2a2a",
    borderRadius: 8,
    color: "#e88",
    fontSize: 13,
    lineHeight: 1.4,
  },
  backupPreview: {
    marginTop: 16,
    padding: 16,
    background: "#141416",
    borderRadius: 10,
    border: "1px solid #2a2a2e",
  },
  backupPreviewHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 12,
  },
  backupPreviewTitle: { fontSize: 15, fontWeight: 600, color: "#e8e4df" },
  backupCategoryBreakdown: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 12,
  },
  backupCatChip: {
    padding: "4px 10px",
    border: "1px solid",
    borderRadius: 12,
    fontSize: 12,
    color: "#c8c4bf",
  },
  backupModeSection: { marginTop: 16 },
  backupModeOptions: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 8,
  },
  backupModeOption: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 8,
    border: "1px solid #2a2a2e",
    cursor: "pointer",
    transition: "all 0.15s",
    background: "transparent",
  },
  backupModeActive: { borderColor: "#C2785C", background: "#C2785C11" },
  backupModeRadio: {
    fontSize: 16,
    color: "#C2785C",
    marginTop: 1,
    flexShrink: 0,
  },
  backupModeLabel: { fontSize: 14, fontWeight: 600, color: "#e8e4df" },
  backupModeDesc: {
    fontSize: 12,
    color: "#7a7a7e",
    lineHeight: 1.4,
    marginTop: 2,
  },
  backupSuccess: {
    marginTop: 16,
    padding: 24,
    background: "#141416",
    borderRadius: 10,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
  },
  backupSuccessText: { fontSize: 15, color: "#c8c4bf", fontWeight: 500 },

  // Profile View
  profileContainer: { maxWidth: 960, margin: "0 auto" },
  profileHero: {
    display: "flex",
    alignItems: "center",
    gap: 24,
    padding: "28px 0",
    borderBottom: "3px solid #333",
    marginBottom: 24,
  },
  profileHeroInfo: { flex: 1, minWidth: 0 },
  profileName: {
    fontSize: 28,
    fontWeight: 700,
    color: "#e8e4df",
    marginBottom: 2,
    letterSpacing: "-0.5px",
  },
  profileRelation: { fontSize: 16, color: "#9a9a9e", marginBottom: 10 },
  profileTagRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  profileTag: {
    padding: "4px 12px",
    borderRadius: 14,
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
  },
  profileActions: { display: "flex", gap: 8, flexShrink: 0 },
  profileEditBtn: {
    padding: "9px 20px",
    background: "#C2785C",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  profileDeleteBtn: {
    padding: "9px 16px",
    background: "transparent",
    border: "1px solid #3a2020",
    color: "#e06060",
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  profileGrid: {
    display: "grid",
    gridTemplateColumns: "1.2fr 0.8fr",
    gap: 20,
    alignItems: "start",
  },
  profileDetailsCol: { display: "flex", flexDirection: "column", gap: 16 },
  profileSideCol: { display: "flex", flexDirection: "column", gap: 16 },
  profileSection: {
    background: "#1a1a1e",
    borderRadius: 12,
    padding: 20,
    border: "1px solid #2a2a2e",
  },
  profileSectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#7a7a7e",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 14,
  },
  profileDetailsList: { display: "flex", flexDirection: "column", gap: 14 },
  profileDetailItem: { display: "flex", alignItems: "flex-start", gap: 12 },
  profileDetailIcon: {
    fontSize: 18,
    width: 24,
    textAlign: "center",
    flexShrink: 0,
    paddingTop: 1,
  },
  profileDetailLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#7a7a7e",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    marginBottom: 1,
  },
  profileDetailValue: { fontSize: 15, color: "#e8e4df" },
  profileDetailSub: { fontSize: 12, color: "#B8945A", marginTop: 2 },
  profileNotes: {
    fontSize: 15,
    color: "#c8c4bf",
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
  },
  profileConnectionItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "background 0.15s",
    marginBottom: 4,
    border: "1px solid #222",
  },
  profileConnectionName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#e8e4df",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  profileConnectionRole: { fontSize: 11, color: "#7a7a7e" },
  profileNightsList: { maxHeight: 200, overflowY: "auto" },
  profileNightsItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 0",
    borderBottom: "1px solid #222",
  },
  profileNightsDate: {
    fontSize: 13,
    color: "#c8c4bf",
    fontWeight: 500,
    minWidth: 110,
  },
  profileNightsLabel: { fontSize: 12, color: "#7a7a7e", fontStyle: "italic" },
  profileQuickStats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
    gap: 10,
  },
  profileQuickStat: {
    textAlign: "center",
    padding: "12px 8px",
    background: "#141416",
    borderRadius: 8,
  },
  profileQuickStatValue: { fontSize: 24, fontWeight: 700, color: "#C2785C" },
  profileQuickStatLabel: { fontSize: 11, color: "#7a7a7e", marginTop: 2 },

  // Detail
  detailRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid #222",
  },
  detailLabel: { fontSize: 13, fontWeight: 600, color: "#7a7a7e" },
  detailValue: { fontSize: 14, color: "#c8c4bf" },
  notesBlock: {
    background: "#141416",
    borderRadius: 8,
    padding: 14,
    fontSize: 14,
    color: "#c8c4bf",
    lineHeight: 1.6,
  },
};

export default styles;
